import { X, Undo2, Trash2 } from 'lucide-react';

interface GuestMeasurementOverlayProps {
  points: { x: number; y: number }[];
  onVideoClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  onCancel: () => void;
  onUndo?: () => void;
  measurementResult?: {
    real_distance_cm: number;
    real_distance_inches: number;
    confidence: number;
  } | null;
  onDeleteMeasurement?: () => void;
}

export function GuestMeasurementOverlay({
  points,
  onVideoClick,
  onCancel,
  onUndo,
  measurementResult,
  onDeleteMeasurement
}: GuestMeasurementOverlayProps) {
  // Calculate midpoint for measurement label
  const midX = points.length === 2 ? (points[0].x + points[1].x) / 2 : 0;
  const midY = points.length === 2 ? (points[0].y + points[1].y) / 2 : 0;

  return (
    <>
      {/* Clickable overlay */}
      <div
        className="absolute inset-0 z-[90] cursor-crosshair"
        onClick={onVideoClick}
        style={{ touchAction: 'none' }}
      />

      {/* Point markers */}
      {points.map((point, idx) => (
        <div
          key={idx}
          className="absolute w-4 h-4 bg-green-500 border-2 border-white rounded-full z-[91] pointer-events-none"
          style={{
            left: `${point.x}%`,
            top: `${point.y}%`,
            transform: 'translate(-50%, -50%)'
          }}
        />
      ))}

      {/* Line between points */}
      {points.length === 2 && (
        <svg
          className="absolute inset-0 z-[90] pointer-events-none"
          style={{ width: '100%', height: '100%' }}
        >
          <line
            x1={`${points[0].x}%`}
            y1={`${points[0].y}%`}
            x2={`${points[1].x}%`}
            y2={`${points[1].y}%`}
            stroke="#22c55e"
            strokeWidth="3"
            strokeDasharray="8,4"
          />
        </svg>
      )}

      {/* Measurement result label - shown below the line */}
      {measurementResult && points.length === 2 && (
        <div
          className="absolute z-[92] pointer-events-none"
          style={{
            left: `${midX}%`,
            top: `${midY + 3}%`, // Offset below the line
            transform: 'translate(-50%, 0)'
          }}
        >
          <div className="bg-green-600/95 backdrop-blur-md text-white px-4 py-2.5 rounded-lg shadow-xl border border-green-400/30">
            <div className="text-center">
              <div className="text-2xl font-bold">
                {measurementResult.real_distance_inches.toFixed(1)}"
              </div>
              <div className="text-xs text-green-100 mt-0.5">
                {measurementResult.real_distance_cm.toFixed(1)} cm
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Instructions - only show when no measurement result */}
      {!measurementResult && (
        <div className="absolute top-4 left-0 right-0 z-[91] flex justify-center pointer-events-none">
          <div className="bg-black/80 backdrop-blur-sm text-white px-4 py-2 rounded-lg text-sm font-medium">
            {points.length === 0 && 'Tap first point'}
            {points.length === 1 && 'Tap second point'}
          </div>
        </div>
      )}

      {/* Control buttons - moved to bottom with blur effect */}
      <div className="absolute bottom-24 left-0 right-0 z-[91] flex justify-center gap-3 px-4">
        <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-full shadow-2xl px-4 py-3 flex items-center gap-2">
          {/* Undo button - only show if there's at least one point and no measurement result */}
          {points.length > 0 && onUndo && !measurementResult && (
            <button
              onClick={onUndo}
              className="w-11 h-11 rounded-full flex items-center justify-center bg-yellow-600 hover:bg-yellow-500 text-white transition-all shadow-lg"
              title="Undo last point"
            >
              <Undo2 className="w-5 h-5" />
            </button>
          )}

          {/* Delete measurement - only show when measurement is complete */}
          {measurementResult && onDeleteMeasurement && (
            <button
              onClick={onDeleteMeasurement}
              className="w-11 h-11 rounded-full flex items-center justify-center bg-red-600 hover:bg-red-700 text-white transition-all shadow-lg"
              title="Delete measurement"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}

          {/* Cancel/Close button */}
          <button
            onClick={onCancel}
            className="w-11 h-11 rounded-full flex items-center justify-center bg-gray-700 hover:bg-gray-600 text-white transition-all shadow-lg"
            title={measurementResult ? "Close" : "Cancel measurement"}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </>
  );
}
